import React, { useEffect, useRef, useState } from 'react';
import { Bug, ChevronLeft, ChevronRight, Mail, X } from 'lucide-react';
import './FlarePage.css';

const screenshots = [
  {
    src: '/flare/flare-1.png',
    alt: 'Flare showing newly posted jobs and an open job detail.',
  },
  {
    src: '/flare/flare-2.png',
    alt: 'Flare showing configured company job boards.',
  },
  {
    src: '/flare/flare-3.png',
    alt: 'Flare settings for job sources, local AI parsing, and search filters.',
  },
];

export default function FlarePage() {
  const [activeScreenshot, setActiveScreenshot] = useState(0);
  const [isSupportOpen, setIsSupportOpen] = useState(false);
  const touchStartX = useRef(null);

  const showPrevious = () => {
    setActiveScreenshot((current) => (current - 1 + screenshots.length) % screenshots.length);
  };

  const showNext = () => {
    setActiveScreenshot((current) => (current + 1) % screenshots.length);
  };

  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'Flare for Mac';
    document.body.classList.add('flare-page-active');

    const closeSupportOnEscape = (event) => {
      if (event.key === 'Escape') setIsSupportOpen(false);
    };
    window.addEventListener('keydown', closeSupportOnEscape);

    return () => {
      document.title = previousTitle;
      document.body.classList.remove('flare-page-active');
      window.removeEventListener('keydown', closeSupportOnEscape);
    };
  }, []);

  const handleCarouselKeyDown = (event) => {
    if (event.key === 'ArrowLeft') showPrevious();
    if (event.key === 'ArrowRight') showNext();
  };

  const handleTouchEnd = (event) => {
    if (touchStartX.current === null) return;
    const distance = event.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(distance) < 45) return;
    if (distance > 0) showPrevious();
    else showNext();
  };

  const supportEmail = 'mailto:daniil.chernopolskii@gmail.com?subject=Flare%20technical%20support&body=Flare%20version%3A%0AmacOS%20version%3A%0A%0AWhat%20happened%3A%0A';

  return (
    <main className="flare-site">
      <section className="flare-intro" aria-labelledby="flare-title">
        <img className="flare-mark" src="/flare/calcifer.png" alt="" />
        <h1 id="flare-title">Flare</h1>
        <p>Flare is built to help you get a job. Get notified as soon as a new role is posted. Free.</p>
      </section>

      <section
        className="flare-carousel"
        aria-label="Flare screenshots"
        aria-roledescription="carousel"
        tabIndex="0"
        onKeyDown={handleCarouselKeyDown}
      >
        <div
          className="flare-carousel-viewport"
          onTouchStart={(event) => { touchStartX.current = event.touches[0].clientX; }}
          onTouchEnd={handleTouchEnd}
        >
          <div className="flare-carousel-track" style={{ transform: `translateX(-${activeScreenshot * 100}%)` }}>
            {screenshots.map((screenshot, index) => (
              <figure key={screenshot.src} aria-hidden={activeScreenshot !== index}>
                <img
                  src={screenshot.src}
                  alt={activeScreenshot === index ? screenshot.alt : ''}
                  loading={index === 0 ? 'eager' : 'lazy'}
                />
              </figure>
            ))}
          </div>
          <button className="flare-carousel-arrow flare-carousel-previous" type="button" onClick={showPrevious} aria-label="Previous screenshot">
            <ChevronLeft aria-hidden="true" />
          </button>
          <button className="flare-carousel-arrow flare-carousel-next" type="button" onClick={showNext} aria-label="Next screenshot">
            <ChevronRight aria-hidden="true" />
          </button>
        </div>
        <div className="flare-carousel-footer">
          <span aria-live="polite">{activeScreenshot + 1} / {screenshots.length}</span>
          <div className="flare-carousel-dots" aria-label="Choose screenshot">
            {screenshots.map((screenshot, index) => (
              <button
                key={screenshot.src}
                type="button"
                className={activeScreenshot === index ? 'is-active' : ''}
                onClick={() => setActiveScreenshot(index)}
                aria-label={`Show screenshot ${index + 1}`}
                aria-current={activeScreenshot === index ? 'true' : undefined}
              />
            ))}
          </div>
        </div>
      </section>

      <aside className={`flare-support ${isSupportOpen ? 'is-open' : ''}`} id="flare-support" aria-hidden={!isSupportOpen}>
        <button className="flare-support-close" type="button" onClick={() => setIsSupportOpen(false)} aria-label="Close technical support">
          <X aria-hidden="true" />
        </button>
        <span>Technical support</span>
        <p>Found something broken? Send the details and your Flare version.</p>
        <a href={supportEmail}><Mail aria-hidden="true" /> Email support</a>
      </aside>
      <button
        className="flare-bug-button"
        type="button"
        onClick={() => setIsSupportOpen((open) => !open)}
        aria-label="Technical support"
        aria-controls="flare-support"
        aria-expanded={isSupportOpen}
      >
        <Bug aria-hidden="true" />
      </button>
    </main>
  );
}
